import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { WrapperStreakModal } from "../components/v2/learning/WrapperStreakModal";
import { TempEventContext } from "./TempEventContext";
import {
  CourseStructureCtx,
  IPassbackDetails,
  PassbackStatus,
  TempEventCtx,
  UserCtx,
} from "../interfaces";
import { useHistory, useLocation } from "react-router-dom";
import { CourseStructureContext } from "./CourseStructureContext";
import { EditUserAccountModal } from "../components/v2/EditUserAccountModal";
import { TermsOfServiceModal } from "../components/v2/TermsOfServiceModal";
import { UserContext } from "./UserContext";
import { SurveyConsentModal } from "../components/v2/utils/SurveyConsentModal";
import { SurveyModal } from "../components/v2/utils/SurveyModal";
import { Role } from "../enums";
import { LoaderDialog } from "../components/v2/utils/LoaderDialog";
import {
  ModalContent,
  SimpleContentModal,
} from "../components/v3/utils/SimpleContentModal";
import { AppBarBanner } from "../components/v3/utils/AppBarBanner";
import { BottomBanner } from "../components/v3/utils/BottomBanner";
import { makeStyles } from "@mui/styles";
import { visuallyHidden } from "@mui/utils";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Slide,
  Stack,
  Typography,
} from "@mui/material";
import axios from "../services/axios";
import { MacsAuthenticator } from "../hooks/useMacsAuthenticator";
import {
  mstDateKey,
  formatDateMst,
  formatTimeMst,
} from "../components/utils/MSTDateTimeFormatter";
import { isSafari } from "../Helpers";

export const UniversalModalContext = createContext({});

const useStyles = makeStyles((theme) => ({
  passbackIndicatorToast: {
    width: "801px",
    height: "74px",
    maxWidth: "100vw",
    position: "relative",
    padding: "25px 23px",
    display: "flex",
    alignItems: "center",
    borderRadius: "16px",
    border: `2px solid ${theme?.palette?.divider ?? "#DEDEDE"}`,
    backgroundColor: "#E2F7FE",
    zIndex: 2000,
  },
  passbackDialog: {
    padding: 32,
    maxWidth: 511,
    height: 181,
    position: "relative",
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: 15,
  },
  buttonPassbackDialog: {
    width: 139,
    height: 50,
    padding: 0,
    fontSize: "19px",
  },
}));

interface SurveyOptions {
  dialogTitle: string;
  dialogBody: string;
  surveyField: string;
  surveyUrl: string;
}

// The Universal Modal provider contains state to control modals that are opened in multiple places
// This allows us to define the modal in one place and call it from any other component
// For example, when opening the streak modal from the home page or from the learning experience
// Another example, the user edit profile modal which opens from the user dropdown,
// a link on home, and the help button
export const UniversalModalProvider: React.FC = ({ children }) => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const tempEventCtx = useContext(TempEventContext) as TempEventCtx;
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const userCtx = useContext(UserContext) as UserCtx;
  const currentUserRole = userCtx.dbUser?.role;
  const orchardLogoutIframeName = "orchardLogoutIframe";
  const orchardLogoutFormRef = useRef<HTMLFormElement | null>(null);
  const orchardLogoutInFlightRef = useRef(false);

  const [streakModalInLearning, setStreakModalInLearning] = useState(false);
  const [userAccountModalOpen, setUserAccountModalOpen] = useState(false);
  const [tosModalOpen, setTosModalOpen] = useState(false);
  const [loaderDialogOpen, setLoaderDialogOpen] = useState(false);
  const [loaderDialogText, setLoaderDialogText] = useState("");
  const [gradePassbackStatus, setGradePassbackStatus] = useState<PassbackStatus>(null);
  const [gradePassbackDetails, setGradePassbackDetails] = useState<
    IPassbackDetails | undefined
  >();
  const [showGradePassbackFailModal, setShowGradePassbackFailModal] = useState(false);
  const [dismissedBrowserBanner, setDismissedBrowserBanner] = useState(false);
  const handleDismissBrowserBanner = () => {
    setDismissedBrowserBanner(true);
  };
  // This is for the v2 surveys
  const [surveyConsentModalOpen, setSurveyConsentModalOpen] = useState(false);
  const [seenConsentModal, setSeenConsentModal] = useState(false);
  const [surveyModalOpen, setSurveyModalOpen] = useState(false);
  const [surveyModalOptions, setSurveyModalOptions] = useState<SurveyOptions>();
  const [simpleContentModalOpen, setSimpleContentModalOpen] = useState(false);
  const [simpleModalContent, setSimpleModalContent] = useState<ModalContent>(
    {} as ModalContent
  );
  const now = new Date();

  const showMaintBanner =
    userCtx.maintenanceWindow &&
    (() => {
      const mw = userCtx.maintenanceWindow;

      const from =
        mw.banner_from instanceof Date ? mw.banner_from : new Date(mw.banner_from);

      const to = mw.banner_to instanceof Date ? mw.banner_to : new Date(mw.banner_to);

      return from <= now && now <= to ? mw : undefined;
    })();

  // demo banner stuff
  const [demoBannerDismissed, setDemoBannerDismissed] = useState(false);

  //

  const userSchool =
    userCtx.dbUser?.user_school && userCtx.dbUser?.user_school?.length > 0
      ? userCtx.dbUser.user_school[0]
      : undefined;
  const schoolMetadata = userSchool?.school?.metadata;
  const useBlueBottleVariant = userSchool?.school?.metadata?.surveys_bluebottle;

  const currentDate = new Date();
  const surveyPostCourseStartDate = schoolMetadata?.surveys_post_startdate
    ? new Date(schoolMetadata?.surveys_post_startdate)
    : new Date("05/01/2024");
  const surveyPostCourseEndDate = schoolMetadata?.surveys_post_enddate
    ? new Date(schoolMetadata?.surveys_post_enddate)
    : new Date("06/01/2024");

  const isInPostCoursePeriod =
    currentDate.getTime() >= surveyPostCourseStartDate.getTime() &&
    currentDate.getTime() <= surveyPostCourseEndDate.getTime();

  const surveyEnrollmentEnded =
    currentDate.getTime() >= surveyPostCourseStartDate.getTime();

  const closeStreakModal = () => {
    tempEventCtx.setShowStreakModal(false);
  };

  const showBrowserBanner = useMemo(() => {
    // feature flag 400 is the Browser Banner - set released to true to show for everyone
    const ff = userCtx.DbFeatureFlags?.get(400);
    if (!ff || !ff.released) return false;

    return isSafari() && ff.released;
  }, [userCtx.DbFeatureFlags]);

  // trigger survey consent modal
  useEffect(() => {
    if (
      !userCtx.dbUser ||
      !userCtx.dbUser?.user_school ||
      !userCtx.dbUser?.user_school.length ||
      seenConsentModal ||
      surveyEnrollmentEnded
    )
      return;

    const userRole = userCtx.dbUser.role;

    const userSchool = userCtx.dbUser.user_school[0];
    const userSurvey = userCtx.dbUser.user_survey;
    if (userSchool.school?.survey_opted && userRole === Role.student) {
      if (
        (userSurvey?.consent === null && userSurvey?.consent_dismissals < 2) ||
        !userSurvey
      ) {
        setSurveyConsentModalOpen(true);
        setSeenConsentModal(true);
      }
    }
  }, [
    userCtx.dbUser,
    userCtx.dbUser?.user_school,
    seenConsentModal,
    surveyEnrollmentEnded,
  ]);

  // trigger course surveys
  useEffect(() => {
    if (
      !userCtx.dbUser ||
      !userCtx.dbUser?.user_school ||
      !userCtx.dbUser?.user_school.length ||
      !userCtx.dbUser.user_survey
    )
      return;

    const userSchool = userCtx.dbUser.user_school[0];
    const userSurvey = userCtx.dbUser.user_survey;
    const userRole = userCtx.dbUser.role;

    // At the time of writing this we had schools still opted in with classes ongoing
    // This flag changes the survey and trigger values depending on if the school/class is part of the new survey group or old survey group

    if (
      userSchool.school?.survey_opted &&
      userRole === Role.student &&
      !surveyConsentModalOpen
    ) {
      // Open pre-course survey
      if (!userSurvey.surveyed_pre_course && !surveyEnrollmentEnded) {
        openSurvey({
          dialogTitle: "It’s time to take a survey!",
          dialogBody: "",
          surveyField: "surveyed_pre_course",
          surveyUrl: useBlueBottleVariant
            ? "https://asu.co1.qualtrics.com/jfe/form/SV_6VW7A73RJLVHFHM" // bb variant
            : "https://asu.co1.qualtrics.com/jfe/form/SV_aY8D4O4ly0Jj2kK",
        });
      }

      // fall mid course survey
      if (!userSurvey.pre_survey_date) return; // don't do anything else until pre course survey is finished
      // Open in increments of 30 days after initial pre_survey
      const checkDateMid = userSurvey.last_mid_survey_date
        ? new Date(userSurvey.last_mid_survey_date)
        : new Date(userSurvey.pre_survey_date);
      // checkDateMid.setDate(
      //   checkDateMid.getDate() + 30 * (userSurvey.times_surveyed_mid_course + 1)
      // );
      checkDateMid.setDate(checkDateMid.getDate() + 30);

      if (
        userSurvey.surveyed_pre_course &&
        !userSurvey.surveyed_post_course &&
        !surveyEnrollmentEnded &&
        Date.now() > checkDateMid.getTime() &&
        !useBlueBottleVariant // blue bottle students don't get mid-course surveys
      ) {
        openSurvey({
          dialogTitle: "It’s time to take a survey!",
          dialogBody: "",
          surveyField: "times_surveyed_mid_course",
          surveyUrl: "https://asu.co1.qualtrics.com/jfe/form/SV_9RjZhdXk5UTyHB4",
        });
      }

      // post course surveys
      if (!userSurvey.surveyed_post_course && isInPostCoursePeriod) {
        openSurvey({
          dialogTitle: "It’s time to take a survey!",
          dialogBody: "",
          surveyField: "surveyed_post_course",
          surveyUrl: useBlueBottleVariant
            ? "https://asu.co1.qualtrics.com/jfe/form/SV_80OPQ8ALgS6sSCa" // blue bottle end course survey
            : "https://asu.co1.qualtrics.com/jfe/form/SV_8ix3yVWJ3z6dqkK",
        });
      }
    }
  }, [
    userCtx.dbUser,
    userCtx.dbUser?.user_school,
    surveyConsentModalOpen,
    isInPostCoursePeriod,
    surveyEnrollmentEnded,
    useBlueBottleVariant,
  ]);

  // trigger user submission course surveys
  // useEffect(() => {
  //   if (
  //     !userCtx.dbUser ||
  //     !userCtx.dbUser?.user_school ||
  //     !userCtx.dbUser?.user_school.length ||
  //     !userCtx.dbUser.user_survey ||
  //     !userCtx.userSubmissions ||
  //     !userCtx.dbUser.user_survey.consent_date ||
  //     !userCtx.dbUser.class_student ||
  //     !userCtx.dbUser.class_student.length
  //   )
  //     return;

  //   const userSchool = userCtx.dbUser.user_school[0];
  //   const userSurvey = userCtx.dbUser.user_survey;

  //   // At the time of writing this we had schools still opted in with classes ongoing
  //   // This flag changes the survey and trigger values depending on if the school/class is part of the new survey group or old survey group

  //   if (
  //     userSchool.school.survey_opted &&
  //     userSurvey.times_surveyed_mid_course < 2

  //   ) {
  //     // get submissions since consenting to survey
  //     const submissions = userCtx.userSubmissions.filter(
  //       (submission) =>
  //         new Date(submission.created_at).getTime() >
  //         new Date(userSurvey.consent_date!).getTime()
  //     );

  //     // Once x number of submissions open survey
  //     // Only trigger twice
  //     if (submissions.length >= (userSurvey.times_surveyed_mid_course + 1) * 20) {
  //       if (userSurvey.times_surveyed_mid_course === 0) {
  //         // mid course survey 1
  //         openSurvey({
  //           dialogTitle: "It’s time to take a survey!",
  //           dialogBody: "",
  //           surveyField: "times_surveyed_mid_course",
  //           surveyUrl: "https://asu.co1.qualtrics.com/jfe/form/SV_9sQFxCrQ3URvY10",
  //         });
  //       } else if (userSurvey.times_surveyed_mid_course === 1) {
  //         // mid course survey 2
  //         openSurvey({
  //           dialogTitle: "It’s time to take a survey!",
  //           dialogBody: "",
  //           surveyField: "times_surveyed_mid_course",
  //           surveyUrl: "https://asu.co1.qualtrics.com/jfe/form/SV_6JApzdJZQqr7eOW",
  //         });
  //       }
  //     }
  //   }
  // }, [userCtx.dbUser, userCtx.dbUser?.user_school, userCtx.userSubmissions]);

  // determine layout of streak modal
  useEffect(() => {
    if (location.pathname === "/solver") {
      setStreakModalInLearning(true);
    } else {
      setStreakModalInLearning(false);
    }
    if (location.pathname.includes("/demo-registration")) {
      setDemoBannerDismissed(true);
    }
  }, [location]);

  const handleGoHome = () => {
    // added this logic to go back to last page from where the user came from
    // if (history.action === "POP") history.push("/learning-overview");
    // else history.goBack();
    const { backTo, search, route } = courseStructureCtx.backBehavior;
    switch (backTo) {
      case "deeplinking":
        // route and search should be set to the params initially set for deep linking
        // this will push them back to previous params/page
        history.push({
          pathname: route || "/lti/deep-linking",
          search: search,
        });
        break;
      case "moduleoverview":
        let module = courseStructureCtx.courseTreeStructure?.getModuleByQuestionId(
          courseStructureCtx.currentQuestionId
        );
        if (!module) {
          history.push({
            pathname: "/learning-overview",
          });
        } else {
          let isStudent = userCtx.dbUser.role === Role.student;
          history.push({
            pathname: isStudent ? "/modules" : "/explore/activities",
            search: new URLSearchParams({
              ...(isStudent
                ? {
                    moduleId: module?.id!,
                  }
                : {
                    module: module?.id!,
                    courseId: module.parent?.id!,
                    content: "1",
                  }),
            }).toString(),
          });
        }
        break;
      case "home":
      default:
        history.push({
          pathname: "/learning-overview",
        });
        break;
    }
  };

  const handleStreakGoHome = () => {
    history.push({
      pathname: "/learning-overview",
    });
  };

  const openSurvey = (options: SurveyOptions) => {
    setSurveyModalOptions(options);
    setSurveyModalOpen(true);
  };

  const passbackContainerRef = useRef<Element>();

  const onPassbackStart = () => {
    setGradePassbackStatus("inprogress");
  };

  const onPassbackFinish = (
    status: PassbackStatus,
    details: IPassbackDetails | undefined
  ) => {
    setGradePassbackStatus(status);
    setGradePassbackDetails(details);
    if (status === "failure") {
      setShowGradePassbackFailModal(true);
    }

    setTimeout(() => {
      setGradePassbackStatus(null);
    }, 2000);
  };

  const handleRetryGradePassback = async () => {
    setShowGradePassbackFailModal(false);
    const gradePassbackToRetry =
      gradePassbackDetails?.ltiAssignment?.digit_resource_id ?? null;
    if (gradePassbackToRetry) {
      try {
        // trigger passback
        let ltik = MacsAuthenticator.authHook.ltik;
        await axios.post(`/lti/1p3/grade?ltik=${ltik}`, {
          linkedAssetId: gradePassbackToRetry,
        });
      } catch (e) {
        console.error(e);
      }
    }
  };

  const handleDismissDemoBanner = () => {
    setDemoBannerDismissed(true);
  };

  const [screenReaderMessage, setScreenReaderMessage] = useState("");

  useEffect(() => {
    const isStudentView = userCtx.authHook.isStudentView;
    if (isStudentView === "true") {
      setScreenReaderMessage("");
      setTimeout(() => {
        setScreenReaderMessage("Entered student view mode");
      }, 100);
    } else {
      setScreenReaderMessage("");
      setTimeout(() => {
        setScreenReaderMessage("Exited student view mode");
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.authHook.isStudentView]);

  const logoutOrchardThen = (callback: () => void) => {
    // Prevent repeated clicks from spamming submissions
    if (orchardLogoutInFlightRef.current) {
      callback();
      return;
    }
    orchardLogoutInFlightRef.current = true;

    try {
      const form = orchardLogoutFormRef.current;
      if (!form) {
        console.warn("Orchard logout form ref missing, falling back to callback");
        callback();
        return;
      }

      // Submit the hidden POST
      form.submit();
    } catch (e) {
      console.warn("Orchard logout form submit failed:", e);
    } finally {
      // We can't observe success/failure cross-origin, so we proceed.
      // Short delay helps ensure the POST is actually dispatched before we redirect/reload.
      setTimeout(() => {
        orchardLogoutInFlightRef.current = false;
        callback();
      }, 2000);
    }
  };

  return (
    <UniversalModalContext.Provider
      value={{
        handleGoHome,
        userAccountModalOpen,
        setUserAccountModalOpen,
        tosModalOpen,
        setTosModalOpen,
        setLoaderDialogOpen,
        setLoaderDialogText,
        setSimpleContentModalOpen,
        setSimpleModalContent,
        onPassbackStart,
        onPassbackFinish,
      }}>
      {!location.pathname.startsWith("/lti/deep-linking") &&
        courseStructureCtx.deeplinkMode !== null && (
          <AppBarBanner
            sticky={true}
            variant="button"
            backgroundColor="#E9F5FE"
            buttonText={"Exit Preview"}
            buttonOnClick={() => {
              if (courseStructureCtx.deeplinkMode) {
                history.push({
                  pathname: courseStructureCtx.deeplinkMode.route,
                  search: courseStructureCtx.deeplinkMode.search,
                });
              } else {
                history.push({
                  pathname: "/learning-overview",
                });
              }
            }}
          />
        )}
      {showBrowserBanner && !dismissedBrowserBanner && (
        <AppBarBanner
          height={60}
          sticky={true}
          variant="text"
          onClose={handleDismissBrowserBanner}
          message={
            <>
              <span style={{ fontWeight: "bold" }}>Browser Compatibility Notice:</span>{" "}
              Please switch to Google Chrome to use this platform.
            </>
          }
          backgroundColor="#D6F0FA"
        />
      )}
      {showMaintBanner && (
        <AppBarBanner
          height={60}
          sticky={true}
          variant="text"
          message={
            showMaintBanner.custom_banner_message
              ? // Optional full override
                showMaintBanner.custom_banner_message
              : (() => {
                  const { banner_from, banner_to } = showMaintBanner;

                  const sameDay = mstDateKey(banner_from) === mstDateKey(banner_to);

                  const fromTime = formatTimeMst(banner_from);
                  const toTime = formatTimeMst(banner_to);
                  const fromDate = formatDateMst(banner_from);
                  const toDate = formatDateMst(banner_to);

                  if (sameDay) {
                    // Same MST calendar day
                    // Digit is undergoing scheduled maintenance from 9:30 PM to 11:30 PM MST on Sunday, October 12.
                    return (
                      <>
                        Digit is undergoing scheduled maintenance from{" "}
                        <b>
                          {fromTime} to {toTime} MST on {fromDate}.
                        </b>{" "}
                        We appreciate your patience during this time.
                      </>
                    );
                  }

                  // Multi-day window in MST
                  // Digit is undergoing scheduled maintenance from 9:30 PM MST on Sunday, October 12 to 11:30 PM MST on Monday, October 13.
                  return (
                    <>
                      Digit is undergoing scheduled maintenance from{" "}
                      <b>
                        {fromTime} MST on {fromDate} to {toTime} MST on {toDate}.
                      </b>{" "}
                      We appreciate your patience during this time.
                    </>
                  );
                })()
          }
          backgroundColor="#D6F0FA"
        />
      )}
      <Box aria-live="assertive" aria-atomic="true" sx={visuallyHidden}>
        {screenReaderMessage}
      </Box>
      {userCtx.authHook.isStudentView === "true" && (
        <BottomBanner
          height={51}
          // width={522}
          onClose={() => {
            logoutOrchardThen(() => {
              userCtx.authHook.setIsStudentView(null);
              if (userCtx.authHook.exitStudentViewPath) {
                window.location.href = userCtx.authHook.exitStudentViewPath;
              } else {
                window.location.reload();
              }
              userCtx.authHook.setExitStudentViewPath(null);
            });
          }}
        />
      )}
      {userCtx.studentCurrentClass?.is_demo && !demoBannerDismissed && (
        <AppBarBanner
          height={60}
          sticky={false}
          onClose={handleDismissDemoBanner}
          variant="text"
          message={
            <Typography variant="body1" fontSize={16} component="span" color={"#191919"}>
              You are currently viewing a demo course.
            </Typography>
          }
          backgroundColor="#D6F0FA"
        />
      )}
      {userCtx.authHook.isStudentView === "true" && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            outline: "8px solid #80449F",
            outlineOffset: "-8px",
            pointerEvents: "none",
            zIndex: 3000,
          }}
        />
      )}
      {children}
      <WrapperStreakModal
        open={tempEventCtx.showStreakModal}
        streakModalInLearning={streakModalInLearning}
        backBehavior={courseStructureCtx.backBehavior}
        handleContinue={() => {
          closeStreakModal();
        }}
        handleGoHome={() => {
          handleStreakGoHome();
          closeStreakModal();
        }}
      />
      <EditUserAccountModal
        open={userAccountModalOpen}
        handleOpen={() => setUserAccountModalOpen(true)}
        handleClose={() => setUserAccountModalOpen(false)}
      />
      <TermsOfServiceModal
        open={tosModalOpen}
        handleOpen={() => setTosModalOpen(true)}
        handleClose={() => setTosModalOpen(false)}
      />
      {currentUserRole === Role.student && (
        <SurveyConsentModal
          promptOpen={surveyConsentModalOpen}
          promptOnClose={() => setSurveyConsentModalOpen(false)}
        />
      )}
      {!surveyConsentModalOpen && surveyModalOptions && (
        <SurveyModal
          dialogTitle={surveyModalOptions.dialogTitle}
          dialogBody={surveyModalOptions.dialogBody}
          surveyField={surveyModalOptions.surveyField}
          surveyUrl={surveyModalOptions.surveyUrl}
          promptOpen={surveyModalOpen}
          promptOnClose={() => setSurveyModalOpen(false)}
        />
      )}
      <LoaderDialog open={loaderDialogOpen} message={loaderDialogText} />
      <SimpleContentModal
        handleClose={() => {
          setSimpleContentModalOpen(false);
          setSimpleModalContent({} as ModalContent);
        }}
        modalOpen={simpleContentModalOpen}
        content={simpleModalContent}
      />
      <Dialog
        fullWidth
        classes={{
          paper: classes.passbackDialog,
        }}
        aria-labelledby="passback-failed-dialog"
        open={showGradePassbackFailModal}>
        <Stack direction="column" justifyContent="space-between" sx={{ height: "100%" }}>
          <Typography variant="body1" fontWeight={400} lineHeight="18.75px">
            Oops! It looks like the grade pass-back failed. Please try again.
          </Typography>
          <Stack direction="row" justifyContent="center" gap="16px" marginTop="auto">
            <Button
              onClick={() => {
                setShowGradePassbackFailModal(false);
              }}
              variant="outlined"
              color="primary"
              size="large"
              className={classes.buttonPassbackDialog}>
              Close
            </Button>
            <Button
              onClick={handleRetryGradePassback}
              variant="contained"
              color="primary"
              size="large"
              className={classes.buttonPassbackDialog}>
              Try again
            </Button>
          </Stack>
        </Stack>
      </Dialog>
      {userCtx.authHook.isStudentView === "true" ? (
        <Box
          sx={{
            position: "fixed",
            bottom: "109px",
            left: "50%",
            transform: "translate(-50%,0px)",
          }}
          ref={passbackContainerRef}>
          <Slide
            in={gradePassbackStatus !== null}
            direction="up"
            mountOnEnter
            unmountOnExit
            container={passbackContainerRef.current}>
            <Box className={classes.passbackIndicatorToast}>
              {gradePassbackStatus === "inprogress" ? (
                <CircularProgress thickness={8} color="primary" size="24px" />
              ) : (
                <Typography variant="body1" fontSize="24px" color="#0092E0">
                  <i className="fa-solid fa-circle-check"></i>
                </Typography>
              )}
              <Typography variant="body1" marginLeft="8px">
                {gradePassbackStatus === "inprogress" ? (
                  "Hold on! We are saving your progress..."
                ) : (
                  <>
                    <span style={{ fontWeight: 700 }}>
                      {gradePassbackDetails?.digitRawScoreMax
                        ? `${gradePassbackDetails?.digitRawScore ?? 0}/${
                            gradePassbackDetails?.digitRawScoreMax ?? 0
                          }`
                        : `${Math.round(gradePassbackDetails?.digitScore ?? 0)}%`}
                    </span>{" "}
                    would be sent to Canvas
                  </>
                )}
              </Typography>
            </Box>
          </Slide>
        </Box>
      ) : (
        <Box
          sx={{
            position: "fixed",
            bottom: "109px",
            left: "50%",
            transform: "translate(-50%,0px)",
          }}
          ref={passbackContainerRef}>
          <Slide
            in={gradePassbackStatus !== null}
            direction="up"
            mountOnEnter
            unmountOnExit
            container={passbackContainerRef.current}>
            <Box className={classes.passbackIndicatorToast}>
              {gradePassbackStatus === "inprogress" ? (
                <CircularProgress thickness={8} color="primary" size="24px" />
              ) : (
                <Typography variant="body1" fontSize="24px" color="#0092E0">
                  <i className="fa-solid fa-circle-check"></i>
                </Typography>
              )}
              <Typography variant="body1" marginLeft="8px">
                {gradePassbackStatus === "inprogress"
                  ? "Hold on! We are saving your progress..."
                  : "All done!"}
              </Typography>
            </Box>
          </Slide>
        </Box>
      )}
      {/* Invisible Orchard logout POST target (avoids CORS by using browser form submit) */}
      <iframe
        title="orchard-logout-target"
        name={orchardLogoutIframeName}
        style={{ display: "none" }}
      />
      <form
        ref={orchardLogoutFormRef}
        method="POST"
        action={`https://${
          process.env.REACT_APP_ORCHARD_HOST ?? "asuorchard.asu.edu"
        }/user/logout/confirm`}
        target={orchardLogoutIframeName}
        style={{ display: "none" }}>
        <input type="hidden" name="confirm" value="1" />
        <input type="hidden" name="form_id" value="user_logout_confirm" />
        <input type="hidden" name="op" value="Log out" />
      </form>
    </UniversalModalContext.Provider>
  );
};
